import { GRID_UNIT_PX, TYPE_PRODUCT, TYPE_TEAMS } from '@/constants';
import Graph from 'node-dijkstra';
import Packet from '@/models/Packet';
import { addNodes, findShortestPath, removeNodes } from '@/utilities/graphHelper';
import calcStraightPathByDistance from '@/utilities/calcStraightPathByDistance';

export default class PathManager {
  constructor() {
    this.cacheGridPath = {};
    this.cacheStraightPath = {};
    this.unpacketList = {};
    this.packetList = [];

    this.graphRowCount = 0;
    this.graphColumnCount = 0;
    this.coordsProhibited = [];
    this.graph = new Graph();
  }

  processByTick() {
    // 可能是 packet 抵達後通知，再更新 packetList
    this.packetList = this.packetList.filter((packet) => !packet.isArrived);

    this.packetList.forEach((packet) => {
      packet.move();
    });

    Object.keys(this.unpacketList).forEach((pathName) => {
      if (this.unpacketList[pathName].list.length) {
        this.createPacket({
          tasks: this.unpacketList[pathName].list,
          sourceTeam: this.unpacketList[pathName].sourceTeam,
          targetTeam: this.unpacketList[pathName].targetTeam,
        });
        this.unpacketList[pathName].list = [];
      }
    });
  }

  addTask({
    task,
    sourceTeam,
    targetTeam,
  }) {
    const pathName = `${this.getGroupName(sourceTeam)}:${this.getGroupName(targetTeam)}`;
    if (!this.unpacketList[pathName]) {
      this.unpacketList[pathName] = {};
      this.unpacketList[pathName].list = [];
      this.unpacketList[pathName].sourceTeam = sourceTeam;
      this.unpacketList[pathName].targetTeam = targetTeam;
    }
    this.unpacketList[pathName].list.push(task);
  }

  createPacket({
    tasks,
    sourceTeam,
    targetTeam,
  }) {
    const targetGroupName = this.getGroupName(targetTeam);
    this.packetList.push(new Packet({
      tasks,
      targetTeam,
      path: this.getGridPath(sourceTeam.taskDeliverCoord, targetTeam.taskReceiveCoord),
      type: this.getPacketType(tasks[0].constructor.name, targetGroupName),
    }));
  }

  // eslint-disable-next-line class-methods-use-this
  getPacketType(taskConstructorName, targetTeamName) {
    switch (taskConstructorName) {
      case 'Bug':
        return targetTeamName.includes(TYPE_TEAMS.QA) ? 'FIX' : 'BUG';
      default:
        return taskConstructorName;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getGroupName(group) {
    return group.constructor.name === 'Product' ? group.constructor.name : group.name;
  }

  getPacketWorkload(teamName) {
    const packets = this.packetList.filter((packet) => packet.targetTeam.name === teamName);
    return packets
      .reduce(
        (acc, packet) => acc + packet.tasks.reduce(
          (accRemainingTime, task) => accRemainingTime + task.remainingTime, 0,
        ),
        0,
      );
  }

  // eslint-disable-next-line class-methods-use-this
  getCacheKey(coord) {
    return `${coord[0]}:${coord[1]}`;
  }

  getCachePath({ startingCoord, endCoord, cache }) {
    const startingKey = this.getCacheKey(startingCoord);
    const endKey = this.getCacheKey(endCoord);
    return cache[startingKey]?.[endKey];
  }

  updateCachePath({
    startingCoord, endCoord, path, cache,
  }) {
    const startingKey = this.getCacheKey(startingCoord);
    const endKey = this.getCacheKey(endCoord);
    const cacheMap = cache;
    if (!cache[startingKey]) {
      cacheMap[startingKey] = {};
    }
    cacheMap[startingKey][endKey] = path;
  }

  getGridPath(startingCoord, endCoord) {
    let path = this.getCachePath({ startingCoord, endCoord, cache: this.cacheGridPath });
    if (!path) {
      path = findShortestPath(this.graph, startingCoord, endCoord);
      this.updateCachePath({
        startingCoord, endCoord, path, cache: this.cacheGridPath,
      });
    }
    return path;
  }

  getStraightPath(startingCoord, endCoord) {
    let path = this.getCachePath({ startingCoord, endCoord, cache: this.cacheStraightPath });
    if (!path) {
      path = calcStraightPathByDistance(startingCoord, endCoord);
      this.updateCachePath({
        startingCoord, endCoord, path, cache: this.cacheStraightPath,
      });
    }
    return path;
  }

  updatePathGraph(team) {
    if (team.type === TYPE_PRODUCT) {
      return;
    }
    this.updateCoordsProhibited(team);

    const teamCanvasConfig = team.canvasConfig.group;
    const teamGridRange = {
      column: (teamCanvasConfig.x + teamCanvasConfig.width) / GRID_UNIT_PX,
      row: (teamCanvasConfig.y + teamCanvasConfig.height) / GRID_UNIT_PX,
    };

    const needAddNodes = teamGridRange.row > this.graphRowCount
      || teamGridRange.column > this.graphColumnCount;
    if (needAddNodes) {
      this.graphRowCount = Math.max(teamGridRange.row, this.graphRowCount);
      this.graphColumnCount = Math.max(teamGridRange.column, this.graphColumnCount);
      addNodes(
        this.graph,
        this.graphRowCount,
        this.graphColumnCount,
      );
    }
    removeNodes(this.graph, this.coordsProhibited);
    this.cacheGridPath = {};
  }

  updateCoordsProhibited(group) {
    const groupCanvasConfig = group.canvasConfig.group;
    const startX = Math.min(groupCanvasConfig.x, groupCanvasConfig.x + groupCanvasConfig.width);
    const startY = Math.min(groupCanvasConfig.y, groupCanvasConfig.y + groupCanvasConfig.height);
    const endX = startX + Math.abs(groupCanvasConfig.width);
    const endY = startY + Math.abs(groupCanvasConfig.height);

    for (let i = startX + GRID_UNIT_PX; i < endX; i += GRID_UNIT_PX) {
      for (let j = startY + GRID_UNIT_PX; j < endY; j += GRID_UNIT_PX) {
        this.coordsProhibited.push(`${i}:${j}`);
      }
    }
  }
}
